import { Processing } from '@paywithextend/components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { Fragment, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  createSetIsToastOpen,
  createSetUnhideToast,
} from 'src/app/Toast/redux/toast.actions'
import {
  AsyncBulkTransactionUpdateCompleteToastMessage,
  AsyncBulkTransactionUpdateInitiatedToastMessage,
  AsyncBulkTransactionUpdatePartialCompleteToastMessage,
  AsyncBulkTransactionUpdateToastMessage,
} from 'src/app/Toast/Toast'
import SvgAlertCircledFilled2 from 'src/components/Svgs/SvgAlertCircledFilled2'
import { HidableToastNames } from 'src/enums'
import {
  stopAsyncJobPoll,
  useActiveAsyncJobIds,
} from 'src/queries/asyncJobs/asyncJobPoll.store'
import { asyncJobQueries } from 'src/queries/asyncJobs/asyncJobs.queries'
import { QueryError } from 'src/queries/network.utils'
import {
  ORG_EXPENSE_REPORTS_QK,
  transactionsQueryKeys,
} from 'src/queries/queries.constants'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { css } from 'styled-components'

interface AsyncJobPollerProps {
  jobId: string
  onDone: (jobId: string) => void
}

/**
 * Polls GET `/asyncJobs/:id` until the bulk update completes and reproduces the
 * toast + cache-invalidation side effects that the deleted
 * `getAsyncTransactionUpdateJobStatus` Redux thunk used to own. Renders nothing.
 *
 * Lifecycle (mirrors transactions.actions.tsx):
 * - on start: an "initiated" toast, gated by the `bulk_select_fast_follows`
 *   remote-config flag (as the thunk gated its pre-fetch toast);
 * - while `remaining !== 0`: an in-progress toast ("X of Y updated");
 * - when `remaining === 0`: unhide the bulk toast, then a partial-complete or
 *   success toast, then invalidate org expense reports + transactions (the old
 *   COMPLETE -> reactQueryInterop case), then signal `onDone`.
 */
const AsyncJobPoller: React.FC<AsyncJobPollerProps> = ({ jobId, onDone }) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { bulk_select_fast_follows } = useSelector(
    selectFeatureFlaggerFromState,
  )

  const initiatedRef = useRef(false)
  const completedRef = useRef(false)

  const { data, error } = useQuery(asyncJobQueries.detail(jobId))

  // Initiated toast — fires once when the poll starts, flag-gated, matching the
  // thunk's pre-fetch toast.
  useEffect(() => {
    if (initiatedRef.current) {
      return
    }
    initiatedRef.current = true
    if (bulk_select_fast_follows) {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <AsyncBulkTransactionUpdateInitiatedToastMessage />,
          hidableToastName: HidableToastNames.BulkTransactionUpdate,
          inProgressColorScheme: true,
          image: (
            <Processing
              css={css`
                transform: scale(1.2);
              `}
            />
          ),
          bulkToast: true,
          isInfinite: true,
        }),
      )
    }
    // Run exactly once on mount; the flag is already resolved by the time a
    // bulk job starts, matching the thunk's synchronous read.
  }, [bulk_select_fast_follows, dispatch])

  // Per-tick lifecycle off the polled job status.
  useEffect(() => {
    if (data === undefined) {
      return
    }
    const { total, successful, remaining } = data

    if (remaining !== 0) {
      // in progress
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <AsyncBulkTransactionUpdateToastMessage
              partial={total - remaining}
              total={total}
            />
          ),
          hidableToastName: HidableToastNames.BulkTransactionUpdate,
          inProgressColorScheme: true,
          image: (
            <Processing
              css={css`
                transform: scale(1.2);
              `}
            />
          ),
          bulkToast: true,
          isInfinite: true,
        }),
      )
      return
    }

    // terminal (remaining === 0) — run once per job
    if (completedRef.current) {
      return
    }
    completedRef.current = true

    // Unhide BEFORE the terminal toast so the createSetIsToastOpen hidden-toast
    // guard does not swallow it (ordering matters — see toast.actions).
    dispatch(createSetUnhideToast(HidableToastNames.BulkTransactionUpdate))
    if (successful !== total) {
      // partial failure
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <AsyncBulkTransactionUpdatePartialCompleteToastMessage
              partial={successful}
              total={total}
            />
          ),
          partialSuccessColorScheme: true,
          image: (
            <SvgAlertCircledFilled2
              css={css`
                transform: scale(0.8);
                margin-left: -10px;
              `}
            />
          ),
        }),
      )
    } else {
      // full success
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <AsyncBulkTransactionUpdateCompleteToastMessage />,
        }),
      )
    }

    // Refresh all data the bulk update could have touched (was the
    // GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_COMPLETE reactQueryInterop case).
    queryClient.invalidateQueries({ queryKey: [ORG_EXPENSE_REPORTS_QK] })
    // lists/details keep the default cancelRefetch: true: the user just ran this
    // bulk update and is watching the list, so read-your-write (restart the fetch
    // to show the edited rows) outweighs coalescing. The always-mounted count
    // below is a badge, so it coalesces instead.
    queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.lists })
    queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.details })
    // counts feeds the always-mounted tasks-count query — reuse any in-flight
    // fetch instead of restarting, so bulk-job bursts can't pile up requests.
    queryClient.invalidateQueries(
      { queryKey: transactionsQueryKeys.counts },
      { cancelRefetch: false },
    )
    queryClient.invalidateQueries({
      queryKey: transactionsQueryKeys.accountingStatisticsFamily,
    })

    onDone(jobId)
  }, [data, dispatch, queryClient, jobId, onDone])

  useEffect(() => {
    const queryError = error as QueryError | null
    if (
      queryError?.status === 401 ||
      queryError?.status === 403 ||
      queryError?.status === 404
    ) {
      onDone(jobId)
    }
  }, [error, jobId, onDone])

  // Transient errors have no toast effect: the legacy thunk showed no toast on
  // a failed status fetch and recovered on the next Pusher event. The
  // refetchInterval poll mirrors that for retryable failures — one request per
  // tick, retry:false on the factory, self-healing on the next successful tick.

  return null
}

/**
 * App-global host that polls every active bulk async job. Mounted once near the
 * app root; reads the active job ids from the `asyncJobPoll` store and renders
 * one isolated <AsyncJobPoller> per id (fixed hook count per job, so concurrent
 * jobs are supported cleanly).
 */
export const AsyncJobPollers: React.FC = () => {
  const activeJobIds = useActiveAsyncJobIds()
  return (
    <Fragment>
      {activeJobIds.map((jobId) => (
        <AsyncJobPoller key={jobId} jobId={jobId} onDone={stopAsyncJobPoll} />
      ))}
    </Fragment>
  )
}

export default AsyncJobPoller
